import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import PageLayout from '../../components/layout/PageLayout/PageLayout';
import styles from './TalentOnboardingPage.module.css';
import EditBasicInfoSection from '../Talent/Profile/components/EditBasicInfoSection';
import EditFilmographySection from '../Talent/Profile/components/EditFilmographySection';
import PortfolioAutoFillBox from '../../shared/components/PortfolioAutoFillBox';
import {
  createTalentProfile,
  addTalentMedia,
  addTalentWork,
  addTalentSkill,
  getSkillTags,
  uploadFile,
} from '../../api';
import { getMe } from '../../api/userApi';
import { dedupeAgainst, nextLocalWorkId } from '../../shared/utils/workDedupe';
import { reissueToken } from '../../api/authApi';
import { saveSkillOrder } from '../../shared/utils/skillOrder';
import { useAuth } from '../../contexts/AuthContext';
import { TALENT_PLACEHOLDER_SQUARE } from '../../shared/utils/profilePlaceholders';

function formatPhone(value) {
  if (!value) return '';
  const digits = String(value).replace(/\D/g, '');
  if (digits.length === 11) return `${digits.slice(0,3)}-${digits.slice(3,7)}-${digits.slice(7)}`;
  if (digits.length === 10) return `${digits.slice(0,3)}-${digits.slice(3,6)}-${digits.slice(6)}`;
  return value;
}

/**
 * 서버에 보내는 성별 값.
 *
 * 예전에는 남성·여성을 한글로 보내 프로필 수정(MALE/FEMALE)과 값이 갈렸다.
 * 한글로 저장된 배우는 성별로 거르는 곳(추천 공고 메일·배우 검색)에서 통째로 빠진다.
 */
const GENDER_MAP = { male: 'MALE', female: 'FEMALE', other: 'OTHER' };

const inputStyle = {
  boxSizing: 'border-box',
  width: '100%',
  height: '60px',
  padding: '0 18px',
  background: '#FCFCFD',
  border: '1px solid #E9E9EC',
  borderRadius: '10px',
  fontFamily: 'Pretendard, sans-serif',
  fontWeight: 400,
  fontSize: '18px',
  letterSpacing: '-0.04em',
  color: '#090909',
  outline: 'none',
};

const fieldLabelStyle = {
  fontFamily: 'Pretendard, sans-serif',
  fontWeight: 500,
  fontSize: '20px',
  letterSpacing: '-0.04em',
  color: '#090909',
};

const cardStyle = {
  boxSizing: 'border-box',
  background: '#FFFFFF',
  border: '1px solid #E9E9EC',
  boxShadow: '2px 2px 10px rgba(0,0,0,0.08)',
  borderRadius: '20px',
  padding: '30px',
};

function Field({ label, required, error, children }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', flex: 1 }}>
      <div style={{ display: 'flex', gap: '4px' }}>
        <span style={fieldLabelStyle}>{label}</span>
        {required && <span style={{ ...fieldLabelStyle, color: '#FFA40D' }}>*</span>}
      </div>
      {children}
      {error && <span style={{ fontFamily: 'Pretendard, sans-serif', fontSize: '13px', color: '#EF4444', letterSpacing: '-0.03em' }}>{error}</span>}
    </div>
  );
}

function StepDots({ current }) {
  return (
    <div style={{ display: 'flex', gap: '12px' }}>
      {[1, 2, 3, 4].map((n) => {
        const isDone = n < current;
        const isActive = n === current;
        return (
          <div key={n} style={{
            width: '40px', height: '40px', borderRadius: '50%',
            background: isDone || isActive ? '#FFD92B' : '#E9E9EC',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            {isDone ? (
              <svg width="16" height="12" viewBox="0 0 16 12" fill="none">
                <path d="M1 5.5L6 10.5L15 1.5" stroke="#090909" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            ) : (
              <span style={{ fontFamily: 'Pretendard, sans-serif', fontWeight: 700, fontSize: '16px', color: isActive ? '#090909' : '#A9A9B2' }}>{n}</span>
            )}
          </div>
        );
      })}
    </div>
  );
}


function CompleteModal({ onProfile, onExplore }) {
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 99999 }}>
      <div className={styles.modalCard}>
        <div style={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
          <button onClick={onExplore} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, lineHeight: 1 }}>
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M2 2l16 16M18 2L2 18" stroke="#E9E9EC" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </button>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '32px', width: '100%' }}>
          <div style={{ width: '54px', height: '54px', background: '#FFFBD5', borderRadius: '30px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <svg width="24" height="18" viewBox="0 0 24 18" fill="none">
              <path d="M2 9l7 7L22 2" stroke="#FFA40D" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
          <span style={{ fontFamily: 'Pretendard, sans-serif', fontWeight: 600, fontSize: '44px', letterSpacing: '-0.04em', textAlign: 'center', color: '#090909' }}>배우 프로필 설정 완료</span>
          <span style={{ fontFamily: 'Pretendard, sans-serif', fontWeight: 400, fontSize: '18px', lineHeight: '150%', letterSpacing: '-0.04em', textAlign: 'center', color: '#555555' }}>
            기본 배우 프로필이 생성되었습니다.<br />이제 공고에 지원하고 새로운 기회를 찾아보세요.
          </span>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', width: '100%' }}>
          <button onClick={onProfile} style={{
            width: '100%', height: '65px',
            background: 'linear-gradient(101.37deg, #FFD92B 0.67%, #FFB517 65.65%)',
            border: 'none', borderRadius: '10px', cursor: 'pointer',
            fontFamily: 'Pretendard, sans-serif', fontWeight: 600, fontSize: '18px', letterSpacing: '-0.04em', color: '#090909',
          }}>
            내 프로필 보러가기 →
          </button>
          <button onClick={onExplore} style={{
            width: '100%', height: '65px',
            background: '#FCFCFD', border: '1px solid #E9E9EC',
            borderRadius: '10px', cursor: 'pointer',
            fontFamily: 'Pretendard, sans-serif', fontWeight: 600, fontSize: '18px', letterSpacing: '-0.04em', color: '#090909',
          }}>
            공고 둘러보기
          </button>
        </div>
      </div>
    </div>
  );
}

function TalentOnboardingPage() {
  const navigate = useNavigate();
  const { setProfile, setUserInfo, switchRole } = useAuth();
  const [step, setStep] = useState(1);
  const [showComplete, setShowComplete] = useState(false);
  const [talentProfileId, setTalentProfileId] = useState(null);
  const [errors, setErrors] = useState({});

  // Step 1
  const [name, setName] = useState('');
  const [nickname, setNickname] = useState('');
  const [gender, setGender] = useState('');
  const [birthdate, setBirthdate] = useState('');
  const [heightVal, setHeightVal] = useState('');
  const [weightVal, setWeightVal] = useState('');
  const [residence, setResidence] = useState('');
  const [bio, setBio] = useState('');
  const [agency, setAgency] = useState('');

  // 연락처 정보 (읽기 전용 — 회원가입 정보)
  const [userEmail, setUserEmail] = useState('');
  const [userPhone, setUserPhone] = useState('');
  const [instagram, setInstagram] = useState('');
  const [youtube, setYoutube] = useState('');

  useEffect(() => {
    getMe().then(data => {
      setUserEmail(data.email || '');
      setUserPhone(formatPhone(data.phone || ''));
    }).catch(() => {});
  }, []);

  // Step 2 - 프로필 사진 최대 5개
  const [photos, setPhotos] = useState([null, null, null, null, null]);

  // Step 3
  const [works, setWorks] = useState([
    { id: 1, year: '', title: '', company: '', mediaType: '', role: '', videoUrl: '', stillcutPreview: null, stillcutFileId: null, stillcutMediaId: null, stillcutFile: null, _isNew: true },
  ]);
  const [skillTags, setSkillTags] = useState([]);
  const [selectedSkills, setSelectedSkills] = useState([]);
  const [customSkill, setCustomSkill] = useState('');

  // // Step 4
  // const [videoFile, setVideoFile] = useState(null);
  // const [videoUrl, setVideoUrl] = useState('');
  // const videoInputRef = useRef(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Step 3 진입 시 스킬 태그 목록 조회
  useEffect(() => {
    if (step === 3) {
      getSkillTags().then(setSkillTags).catch(() => {});
    }
  }, [step]);

  const handlePortfolioParsed = (data) => {
    if (!data) return;

    if (data.name) setName(data.name);
    if (data.stage_name) setNickname(data.stage_name);
    if (data.gender) {
      const g = data.gender.trim();
      if (g === '남' || g === '남성' || g.toLowerCase() === 'male') setGender('male');
      else if (g === '여' || g === '여성' || g.toLowerCase() === 'female') setGender('female');
    }
    if (data.birth_date) setBirthdate(data.birth_date.slice(0, 10));
    if (data.height_cm) setHeightVal(String(data.height_cm));
    if (data.weight_kg) setWeightVal(String(data.weight_kg));
    if (data.residence) setResidence(data.residence);
    if (data.affiliation) setAgency(data.affiliation);
    if (data.bio) setBio(data.bio);
    if (data.instagram_url) {
      const val = data.instagram_url.replace(/^https?:\/\/(www\.)?instagram\.com\/@?/, '').replace(/\/$/, '');
      setInstagram(val);
    }
    if (data.youtube_url) setYoutube(data.youtube_url);

    if (Array.isArray(data.skills) && data.skills.length > 0) {
      setSelectedSkills(data.skills);
    }

    if (Array.isArray(data.works) && data.works.length > 0) {
      // 온보딩도 파일을 두 번 올릴 수 있어 병합·중복 제거가 필요하다
      setWorks(prev => {
        const parsed = data.works.map(w => ({
          year: w.year ? String(w.year) : '',
          title: w.title || '',
          company: w.production_company || w.channel || '',
          mediaType: w.category || '',
          role: w.role || '',
          videoUrl: '',
          stillcutPreview: null,
          stillcutFileId: null,
          stillcutMediaId: null,
          stillcutFile: null,
          _isNew: true,
        }));
        // 아직 아무것도 입력하지 않은 빈 행은 자리만 차지하므로 걷어낸다
        const filled = prev.filter(w => w.title || w.year || w.company || w.role);
        const { added } = dedupeAgainst(filled, parsed);
        let seed = nextLocalWorkId(filled);
        return [...filled, ...added.map(w => ({ ...w, id: seed-- }))];
      });
    }
  };

  const handleNext = async () => {
    if (step === 1) {
      const newErrors = {};
      if (!name.trim()) newErrors.name = '이름을 입력해주세요.';
      if (!gender) newErrors.gender = '성별을 선택해주세요.';
      if (!birthdate) newErrors.birthdate = '생년월일을 입력해주세요.';
      if (!heightVal) newErrors.heightVal = '키를 입력해주세요.';
      if (!weightVal) newErrors.weightVal = '몸무게를 입력해주세요.';
      if (Object.keys(newErrors).length > 0) {
        setErrors(newErrors);
        return;
      }
      setErrors({});
    }

    if (step === 2) {
      setErrors({});
    }

    setIsSubmitting(true);
    try {
      if (step === 1) {
        // Step 1: 배우 프로필 생성 → talentProfileId 저장
        const result = await createTalentProfile({
          name,
          gender: GENDER_MAP[gender] || gender,
          birthDate: birthdate,
          stageName: nickname || undefined,
          heightCm: parseInt(heightVal) || undefined,
          weightKg: parseInt(weightVal) || undefined,
          residence: residence || undefined,
          bio: bio || undefined,
          agency: agency || undefined,
          instagramUrl: instagram || undefined,
          youtubeUrl: youtube || undefined,
        });
        setTalentProfileId(result.id);
        setProfile(result.id);
        setStep(2);

      } else if (step === 2) {
        // Step 2: 프로필 사진들 업로드
        for (let i = 0; i < photos.length; i++) {
          if (photos[i]) {
            const { fileEntityId: fileId, url: uploadedUrl } = await uploadFile(photos[i], 'IMAGE');
            await addTalentMedia({
              talentProfileId,
              fileEntityId: fileId,
              mediaType: 'IMAGE',
              photoType: i === 0 ? 'PROFILE_CLOSE_UP' : 'PORTFOLIO',
            });
            if (i === 0 && uploadedUrl) {
              setUserInfo({ profileImage: uploadedUrl });
            }
          }
        }
        setStep(3);

      } else if (step === 3) {
        // Step 3: 특기 저장
        if (talentProfileId && selectedSkills.length > 0) {
          for (const skillName of selectedSkills) {
            const matchedTag = skillTags.find((tag) => tag.name === skillName);
            await addTalentSkill({
              talentProfileId,
              skillTagId: matchedTag?.id ?? null,
              customSkill: matchedTag ? null : skillName,
            });
          }
        }
        // 선택 순서 저장
        saveSkillOrder(talentProfileId, selectedSkills);
        setStep(4);

      } else if (step === 4) {
        // Step 4: 필모그래피 저장
        for (const [i, w] of works.entries()) {
          if (w.title.trim()) {
            const createdWork = await addTalentWork({
              talentProfileId,
              title: w.title,
              role: w.role,
              company: w.company || undefined,
              mediaType: w.mediaType || undefined,
              videoUrl: w.videoUrl || undefined,
              year: parseInt(w.year) || null,
              sortOrder: i,
            });

            if (w.stillcutFile && createdWork?.id) {
              const { fileEntityId } = await uploadFile(w.stillcutFile, 'IMAGE');
              await addTalentMedia({
                talentProfileId,
                fileEntityId,
                mediaType: 'IMAGE',
                photoType: 'FILMOGRAPHY',
                workId: createdWork.id,
              });
            }
          }
        }
        // 온보딩 완료 후 토큰 재발급 (백엔드가 isTalent=true 세팅 → 새 JWT 필요)
        try { await reissueToken(); } catch {}
        switchRole('TALENT');
        localStorage.setItem('isTalent', 'true');
        setShowComplete(true);

      // } else if (step === 4) {
      //   // Step 4: 자기소개 영상 (파일 또는 URL)
      //   if (videoFile) {
      //     const { fileEntityId: fileId } = await uploadFile(videoFile, 'VIDEO');
      //     await addTalentMedia({
      //       talentProfileId,
      //       fileEntityId: fileId,
      //       mediaType: 'VIDEO_FILE',
      //       isIntroVideo: true,
      //     });
      //   } else if (videoUrl.trim()) {
      //     await addTalentMedia({
      //       talentProfileId,
      //       videoUrl: videoUrl.trim(),
      //       mediaType: 'VIDEO_LINK',
      //       isIntroVideo: true,
      //     });
      //   }
      //   setShowComplete(true);
      }
    } catch (error) {
      console.error('[TalentOnboarding] step failed:', step, error);
      alert('저장에 실패했습니다. 다시 시도해주세요.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSkip = async () => {
    if (step < 4) {
      setStep((s) => s + 1);
    } else {
      // Step 4 건너뛰기: 필모그래피 없이 완료 처리
      try { await reissueToken(); } catch {}
      switchRole('TALENT');
      localStorage.setItem('isTalent', 'true');
      setShowComplete(true);
    }
  };

  const navNextLabel = [
    '다음 단계: 프로필 사진 업로드',
    '다음 단계: 경력 및 특기',
    '다음 단계: 필모그래피',
    '프로필 설정 완료',
  ];

  const stepTitles = ['기본 정보', '프로필 사진', '특기', '필모그래피'];
  const stepSubtitles = [
    '기본적인 프로필 정보를 입력하세요.',
    '캐스팅 담당자에게 노출될 사진을 업로드해주세요.',
    '특기를 입력해주세요.',
    '출연작품을 추가하세요.',
  ];

  const renderContent = () => {
    const errBorder = (key) => errors[key] ? { border: '1px solid #EF4444' } : {};

    if (step === 1) return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
        <PortfolioAutoFillBox onParsed={handlePortfolioParsed} />
        <div style={{ ...cardStyle, display: 'flex', flexDirection: 'column', gap: '32px' }}>
        <div className={styles.twoCol}>
          <Field label="이름" required error={errors.name}>
            <input value={name} onChange={(e) => { setName(e.target.value); setErrors(p => ({ ...p, name: '' })); }} placeholder="입력하세요." style={{ ...inputStyle, ...errBorder('name') }} />
          </Field>
          <Field label="예명">
            <input value={nickname} onChange={(e) => setNickname(e.target.value)} placeholder="입력하세요." style={inputStyle} />
          </Field>
        </div>
        <div className={styles.twoCol}>
          <Field label="성별" required error={errors.gender}>
            <div style={{ position: 'relative' }}>
              <select value={gender} onChange={(e) => { setGender(e.target.value); setErrors(p => ({ ...p, gender: '' })); }} style={{ ...inputStyle, ...errBorder('gender'), appearance: 'none', cursor: 'pointer', color: gender ? '#090909' : '#888896' }}>
                <option value="" disabled>성별선택</option>
                <option value="male">남성</option>
                <option value="female">여성</option>
              </select>
              <svg style={{ position: 'absolute', right: '18px', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }} width="14" height="8" viewBox="0 0 14 8" fill="none">
                <path d="M1 1l6 6 6-6" stroke="#090909" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
          </Field>
          <Field label="생년월일" required error={errors.birthdate}>
            <input type="date" value={birthdate} onChange={(e) => { setBirthdate(e.target.value); setErrors(p => ({ ...p, birthdate: '' })); }} style={{ ...inputStyle, ...errBorder('birthdate'), color: birthdate ? '#090909' : '#888896' }} />
          </Field>
        </div>
        <div className={styles.twoCol}>
          <Field label="키(cm)" required error={errors.heightVal}>
            <input type="number" value={heightVal} onChange={(e) => { setHeightVal(e.target.value); setErrors(p => ({ ...p, heightVal: '' })); }} placeholder="입력하세요." style={{ ...inputStyle, ...errBorder('heightVal') }} />
          </Field>
          <Field label="몸무게(kg)" required error={errors.weightVal}>
            <input type="number" value={weightVal} onChange={(e) => { setWeightVal(e.target.value); setErrors(p => ({ ...p, weightVal: '' })); }} placeholder="입력하세요." style={{ ...inputStyle, ...errBorder('weightVal') }} />
          </Field>
        </div>
        <div className={styles.twoCol}>
          <Field label="거주지">
            <input value={residence} onChange={(e) => setResidence(e.target.value)} placeholder="예: 서울시 마포구" style={inputStyle} />
          </Field>
          <Field label="소속">
            <input value={agency} onChange={(e) => setAgency(e.target.value)} placeholder="소속사 또는 기관명" style={inputStyle} />
          </Field>
        </div>
        <Field label="소개">
          <textarea
            value={bio}
            onChange={(e) => setBio(e.target.value)}
            placeholder="자신을 간략하게 소개해 주세요."
            style={{ ...inputStyle, height: '120px', padding: '16px 18px', resize: 'vertical' }}
          />
        </Field>
      </div>

      {/* 연락처 정보 */}
      <div>
        <span style={{ fontFamily: 'Pretendard, sans-serif', fontWeight: 600, fontSize: '24px', letterSpacing: '-0.04em', color: '#090909' }}>연락처 정보</span>
        <span style={{ fontFamily: 'Pretendard, sans-serif', fontWeight: 400, fontSize: '14px', letterSpacing: '-0.04em', color: '#A2A2A2', marginTop: '8px', display: 'block' }}>프로듀서가 연락할 수 있는 정보를 입력하세요.</span>
      </div>
      <div style={{ ...cardStyle, display: 'flex', flexDirection: 'column', gap: '32px' }}>
        <div className={styles.twoCol}>
          <Field label="이메일">
            <input value={userEmail} readOnly disabled style={{ ...inputStyle, color: '#A2A2A2', background: '#F5F5F7', cursor: 'not-allowed' }} />
          </Field>
          <Field label="연락처">
            <input value={userPhone} readOnly disabled style={{ ...inputStyle, color: '#A2A2A2', background: '#F5F5F7', cursor: 'not-allowed' }} />
          </Field>
        </div>
        <div className={styles.twoCol}>
          <Field label="인스타그램">
            <div style={{ position: 'relative', width: '100%' }}>
              <span style={{ position: 'absolute', left: '18px', top: '50%', transform: 'translateY(-50%)', fontSize: '18px', fontWeight: 500, color: '#090909', pointerEvents: 'none', userSelect: 'none' }}>@</span>
              <input
                value={instagram}
                onChange={(e) => { let v = e.target.value; if (v.startsWith('@')) v = v.slice(1); setInstagram(v); }}
                placeholder="아이디 입력"
                style={{ ...inputStyle, paddingLeft: '36px' }}
              />
            </div>
          </Field>
          <Field label="유튜브">
            <input
              value={youtube}
              onChange={(e) => setYoutube(e.target.value)}
              placeholder="https://youtube.com/@채널명"
              style={inputStyle}
            />
          </Field>
        </div>
      </div>
      </div>
    );

    if (step === 2) {
      const filledCount = photos.filter(Boolean).length;
      return (
        <div style={{ ...cardStyle, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '18px' }}>
          <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap', width: '100%' }}>
            {photos.map((photo, i) => {
              const isFirst = i === 0;
              // 정면(0)·추가사진1(1)은 항상 활성, 이후는 바로 앞 슬롯이 채워져야 활성
              const isAvailable = i <= 1 || photos[i - 1] !== null;
              const preview = photo ? URL.createObjectURL(photo) : null;
              return (
                <div key={i} style={{ display: 'flex', flexDirection: 'column', gap: '8px', alignItems: 'center' }}>
                  <span style={{ fontFamily: 'Pretendard, sans-serif', fontWeight: 500, fontSize: '14px', letterSpacing: '-0.04em', color: '#090909' }}>
                    {isFirst ? '정면 프로필 사진' : `추가 사진 ${i}`}
                  </span>
                  <div style={{ position: 'relative', width: '140px', height: '186px' }}>
                    <label style={{
                      display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center',
                      width: '140px', height: '186px',
                      background: photo ? 'transparent' : '#FCFCFD',
                      border: `1px ${photo ? 'solid #090909' : 'dashed #E9E9EC'}`, borderRadius: '10px',
                      cursor: isAvailable ? 'pointer' : 'default',
                      overflow: 'hidden', position: 'relative',
                      opacity: !isAvailable && !photo ? 0.4 : 1,
                    }}>
                      {preview ? (
                        <img src={preview} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      ) : isFirst ? (
                        <img src={TALENT_PLACEHOLDER_SQUARE} alt="기본 프로필" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      ) : (
                        <>
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                            <path d="M12 5v14M5 12h14" stroke="#A9A9B2" strokeWidth="2" strokeLinecap="round"/>
                          </svg>
                          <span style={{ fontFamily: 'Pretendard, sans-serif', fontSize: '12px', color: '#A9A9B2', marginTop: '6px' }}>추가</span>
                        </>
                      )}
                      <input
                        type="file" accept="image/jpeg,image/png" style={{ display: 'none' }}
                        disabled={!isAvailable}
                        onChange={(e) => {
                          const f = e.target.files?.[0];
                          if (f) setPhotos(prev => prev.map((p, idx) => idx === i ? f : p));
                        }}
                      />
                    </label>
                    {photo && (
                      <button
                        onClick={() => setPhotos(prev => prev.map((p, idx) => idx === i ? null : p))}
                        style={{ position: 'absolute', top: 8, right: 8, width: 28, height: 28, borderRadius: '50%', background: 'rgba(0,0,0,0.28)', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2 }}
                      >
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none"><path d="M18 6L6 18M6 6l12 12" stroke="#fff" strokeWidth="2.5" strokeLinecap="round"/></svg>
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          <span style={{ fontFamily: 'Pretendard, sans-serif', fontSize: '13px', color: '#A2A2A2' }}>최대 5개까지 추가 가능합니다. ({filledCount}/5)</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', width: '100%', justifyContent: 'center' }}>
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <circle cx="10" cy="10" r="9" stroke="#090909" strokeWidth="1.5" />
              <path d="M10 9v5M10 7v.5" stroke="#090909" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
            <span style={{ fontFamily: 'Pretendard, sans-serif', fontWeight: 600, fontSize: '14px', letterSpacing: '-0.04em', color: '#090909' }}>사진 가이드</span>
            <span style={{ fontFamily: 'Pretendard, sans-serif', fontWeight: 400, fontSize: '14px', letterSpacing: '-0.04em', color: '#555555' }}>얼굴이 명확하게 보이는 자연스러운 사진을 권장합니다. 과도한 보정은 피해주세요.</span>
          </div>
        </div>
      );
    }

    if (step === 3) return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '40px' }}>
        <EditBasicInfoSection
          selectedSkills={selectedSkills}
          setSelectedSkills={setSelectedSkills}
          customSkill={customSkill}
          setCustomSkill={setCustomSkill}
          skillsOnly
          hideHeader
        />
      </div>
    );

    if (step === 4) return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '40px' }}>
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '-58px', marginBottom: '-20px' }}>
          <button
            type="button"
            onClick={() =>
              setWorks((prev) => {
                const nextId = prev.length > 0 ? Math.max(...prev.map((w) => w.id)) + 1 : 1;
                return [...prev, {
                  id: nextId,
                  year: '',
                  title: '',
                  company: '',
                  mediaType: '',
                  role: '',
                  videoUrl: '',
                  stillcutPreview: null,
                  stillcutFileId: null,
                  stillcutMediaId: null,
                  stillcutFile: null,
                  _isNew: true,
                }];
              })
            }
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              padding: '15px 18px',
              height: '50px',
              background: '#FFFFFF',
              border: '1px solid #eeeeee',
              boxShadow: '0 4px 10px rgba(0, 0, 0, 0.05)',
              borderRadius: '10px',
              cursor: 'pointer',
              fontFamily: 'Pretendard, sans-serif',
              fontSize: '18px',
              fontWeight: 600,
              color: '#111111',
              letterSpacing: '-0.04em',
            }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
              <path d="M12 5v14M5 12h14" stroke="#111111" strokeWidth="2" strokeLinecap="round" />
            </svg>
            추가
          </button>
        </div>
        <EditFilmographySection
          talentProfileId={talentProfileId}
          works={works}
          setWorks={setWorks}
          hideHeader
          hideAddButton
          deferStillcutUpload
        />
      </div>
    );
  };

  return (
    <PageLayout>
      <div className={styles.pageContent}>
        {/* 헤더: 배우 프로필 설정 + 스텝 점 */}
        <div className={styles.pageHeader}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '8px 14px', background: '#E9E9EC', borderRadius: '8px' }}>
              <span style={{ fontFamily: 'Pretendard, sans-serif', fontWeight: 600, fontSize: '16px', letterSpacing: '-0.04em', color: '#090909' }}>배우 / 모델</span>
            </div>
            <h1 style={{ fontFamily: 'Pretendard, sans-serif', fontWeight: 600, fontSize: '36px', letterSpacing: '-0.04em', color: '#090909', margin: 0 }}>프로필 설정</h1>
          </div>
          <StepDots current={step} />
        </div>

        {/* 섹션 타이틀 */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '18px' }}>
          <span style={{ fontFamily: 'Pretendard, sans-serif', fontWeight: 600, fontSize: '24px', letterSpacing: '-0.04em', color: '#090909' }}>{stepTitles[step - 1]}</span>
          <span style={{ fontFamily: 'Pretendard, sans-serif', fontWeight: 400, fontSize: '14px', letterSpacing: '-0.04em', color: '#A2A2A2' }}>{stepSubtitles[step - 1]}</span>
        </div>

        {/* 스텝 콘텐츠 */}
        {renderContent()}

        {/* 하단 네비게이션 */}
        <div style={{ marginTop: '40px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px' }}>
          <div style={{ display: 'flex', gap: '14px', width: '100%' }}>
            {step > 1 && (
              <button onClick={() => setStep((s) => s - 1)} style={{
                flex: 1, height: '60px', background: '#E9E9EC', border: 'none', borderRadius: '10px',
                cursor: 'pointer', fontFamily: 'Pretendard, sans-serif', fontWeight: 600,
                fontSize: '18px', letterSpacing: '-0.04em', color: '#A2A2A2',
              }}>이전으로</button>
            )}
            <button onClick={handleNext} disabled={isSubmitting} style={{
              flex: 1, height: '60px', background: isSubmitting ? '#A2A2A2' : '#090909', border: 'none', borderRadius: '10px',
              cursor: isSubmitting ? 'default' : 'pointer', fontFamily: 'Pretendard, sans-serif', fontWeight: 600,
              fontSize: '18px', letterSpacing: '-0.04em', color: '#FFFFFF',
            }}>
              {isSubmitting ? '처리 중...' : navNextLabel[step - 1]}
            </button>
          </div>
          {(step === 2 || step === 3 || step === 4) && (
            <span onClick={handleSkip} style={{ fontFamily: 'Pretendard, sans-serif', fontSize: '16px', color: '#A2A2A2', cursor: 'pointer', letterSpacing: '-0.04em' }}>건너뛰기</span>
          )}
        </div>
      </div>

      {showComplete && (
        <CompleteModal
          onProfile={() => navigate('/talent/profile')}
          onExplore={() => navigate('/castings')}
        />
      )}
    </PageLayout>
  );
}

export default TalentOnboardingPage;
